import { RefreshCw } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { ConnectGitHubPrompt } from '@/components/integration/ConnectGitHubGate'
import { Panel, PageHeader } from '@/components/ui/Panel'
import { Button, Pagination, Select } from '@/components/ui/Controls'
import { EmptyState, QueryBoundary, TableSkeleton } from '@/components/ui/States'
import { LatencyLegend } from '@/components/ui/LatencyBar'
import { ExecutionDrawer } from '@/components/tools/ExecutionDrawer'
import { ExecutionTable } from '@/components/tools/ExecutionTable'
import { DEFAULT_EXECUTION_FILTERS, useExecutions } from '@/features/executions/useExecutions'
import { useMarkExecutionsSeen } from '@/features/executions/useUnseenExecutions'
import { useAuth } from '@/features/auth/useAuth'
import { useToolCatalog } from '@/features/system/useSystemStatus'
import type { ExecutionFilters } from '@/types/domain'

const STATUS_OPTIONS = [
  { value: '', label: 'any' },
  { value: 'SUCCESS', label: 'success' },
  { value: 'FAILED', label: 'failed' },
  { value: 'DENIED', label: 'denied by policy' },
  { value: 'CONFIRMATION_PENDING', label: 'awaiting approval' },
  { value: 'REJECTED', label: 'rejected' },
  { value: 'VALIDATION_FAILED', label: 'invalid arguments' },
]

const RISK_OPTIONS = [
  { value: '', label: 'any' },
  { value: 'READ', label: 'read' },
  { value: 'WRITE', label: 'write' },
  { value: 'META', label: 'meta' },
]

export function ExecutionsPage() {
  const { user } = useAuth()
  // Opening this page is what "seen" means. Marked once per mount, and only from here —
  // the badge must not clear itself from a page the user never looked at.
  const markSeen = useMarkExecutionsSeen()
  const marked = useRef(false)
  useEffect(() => {
    if (marked.current) return
    marked.current = true
    markSeen.mutate()
    // Deliberately not depending on the mutation object: it is recreated every render, and
    // the ref is what makes this once-per-mount rather than once-per-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [filters, setFilters] = useState<ExecutionFilters>(DEFAULT_EXECUTION_FILTERS)
  const [selected, setSelected] = useState<string | null>(null)
  const { data, isLoading, error, refetch, isFetching } = useExecutions(filters)
  const { data: catalog } = useToolCatalog()

  const toolOptions = useMemo(
    () => [
      { value: '', label: 'any' },
      ...(catalog?.tools ?? []).map((tool) => ({ value: tool.name, label: tool.name })),
    ],
    [catalog],
  )

  const update = (patch: Partial<ExecutionFilters>) =>
    setFilters((current) => ({ ...current, ...patch, offset: 0 }))

  const rows = data?.items ?? []
  const filtered = Boolean(filters.tool || filters.status || filters.risk)

  return (
    <div className="flex min-h-0 flex-col gap-0 lg:flex-row lg:gap-4">
      <div className="min-w-0 flex-1">
        <PageHeader
          title="Executions"
          description="Every MCP tool call made on your behalf, with the outcome the model saw and where the time went."
          actions={
            <Button
              size="sm"
              icon={RefreshCw}
              onClick={() => void refetch()}
              disabled={isFetching}
              className={isFetching ? '[&>svg]:animate-spin' : undefined}
            >
              {isFetching ? 'Refreshing' : 'Refresh'}
            </Button>
          }
        />

        <Panel
          title="History"
          description={data ? `${data.total.toLocaleString('en-US')} recorded calls` : undefined}
          actions={<LatencyLegend />}
          bodyClassName="min-h-[24rem]"
        >
          <div className="flex flex-wrap items-center gap-4 border-b border-line px-4 py-2.5">
            <Select
              label="tool"
              options={toolOptions}
              value={filters.tool ?? ''}
              onChange={(event) => update({ tool: event.target.value || undefined })}
            />
            <Select
              label="status"
              options={STATUS_OPTIONS}
              value={filters.status ?? ''}
              onChange={(event) =>
                update({ status: (event.target.value || undefined) as ExecutionFilters['status'] })
              }
            />
            <Select
              label="risk"
              options={RISK_OPTIONS}
              value={filters.risk ?? ''}
              onChange={(event) =>
                update({ risk: (event.target.value || undefined) as ExecutionFilters['risk'] })
              }
            />
            {filtered ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters(DEFAULT_EXECUTION_FILTERS)}
              >
                Clear filters
              </Button>
            ) : null}
          </div>

          <QueryBoundary
            isLoading={isLoading}
            error={error}
            onRetry={() => void refetch()}
            isEmpty={rows.length === 0}
            loadingFallback={<TableSkeleton rows={10} columns={7} />}
            emptyState={
              filtered ? (
                <EmptyState
                  title="No calls match these filters."
                  hint="Widen the filters, or clear them to see the full history."
                  action={
                    <Button size="sm" onClick={() => setFilters(DEFAULT_EXECUTION_FILTERS)}>
                      Clear filters
                    </Button>
                  }
                />
              ) : user?.github_connected ? (
                <EmptyState
                  title="No tool calls yet."
                  hint="Ask a question in Chat, or connect an MCP host such as Claude Desktop to this server. Every call it makes will appear here."
                />
              ) : (
                // Empty because nothing *can* run yet, not because nothing has. Telling
                // someone to ask a question in Chat would send them to a tool that is
                // going to refuse for the reason nobody has named on screen.
                <ConnectGitHubPrompt what="Your tool calls" />
              )
            }
          >
            <ExecutionTable
              rows={rows}
              selectedRequestId={selected}
              onSelect={(row) => setSelected(row.request_id)}
            />
            <Pagination
              total={data?.total ?? 0}
              limit={filters.limit}
              offset={filters.offset}
              onChange={(offset) => setFilters((current) => ({ ...current, offset }))}
            />
          </QueryBoundary>
        </Panel>
      </div>

      {selected ? (
        <div className="mt-4 min-w-0 lg:mt-0 lg:w-[26rem] lg:shrink-0">
          <div className="lg:sticky lg:top-6">
            <ExecutionDrawer requestId={selected} onClose={() => setSelected(null)} />
          </div>
        </div>
      ) : null}
    </div>
  )
}
